import type { MailMessageView } from "@kwp/contracts";
import { useState } from "react";
import { authHeaders, type TokenSource } from "../../lib/api";
import { firebaseTokenSource } from "../../lib/auth-token";
import { useClients } from "../../lib/clients";
import { useToast } from "../../ui/toast";
import { fileSize } from "./model";

/**
 * Attachments of an open message, each a download (architecture §6.4): the bytes come live from
 * the provider through `GET /mail/attachments/{messageId}/{attachmentId}` with the bearer token
 * (a fetch, so the token never goes in a URL), then are saved from memory as a file.
 */
export type Attachment = MailMessageView["attachments"][number];

export function attachmentUrl(apiUrl: string, messageId: string, attachmentId: string): string {
  return `${apiUrl.replace(/\/+$/, "")}/mail/attachments/${encodeURIComponent(messageId)}/${encodeURIComponent(attachmentId)}`;
}

const FAILURES: Record<string, string> = {
  attachment: "That attachment is no longer in the mailbox.",
  account: "That message is not in one of your mailboxes.",
  provider_refused_token: "This account needs to be reconnected in Settings → Accounts.",
};

/** Fetches one attachment's bytes; throws an Error a person can read. */
export async function fetchAttachment(fetcher: typeof fetch, apiUrl: string, getToken: TokenSource, messageId: string, attachmentId: string): Promise<Blob> {
  const res = await fetcher(attachmentUrl(apiUrl, messageId, attachmentId), { headers: await authHeaders(getToken), cache: "no-store" });
  if (res.ok) return res.blob();
  const body = (await res.json().catch(() => null)) as { error?: { reason?: string } } | null;
  const reason = body?.error?.reason;
  const known = reason === undefined ? undefined : FAILURES[reason];
  throw new Error(known === undefined ? `The download failed (${res.status}).` : known);
}

/** Saves a blob as a file through a temporary object URL. */
export function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

export function Attachments({ messageId, attachments }: { messageId: string; attachments: readonly Attachment[] }) {
  const files = attachments.filter((a) => !a.inline);
  if (files.length === 0) return null;
  return (
    <div className="mt-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted">Attachments</h3>
      <ul aria-label="Attachments" className="mt-1 flex flex-wrap gap-2">
        {files.map((a, i) => (
          <li key={`${a.name}-${i}`}>
            {a.id === null ? (
              <span className="inline-block rounded-md border border-border px-2 py-1 text-xs text-text">
                {a.name} <span className="text-muted">· {fileSize(a.size)}</span>
              </span>
            ) : (
              <DownloadButton messageId={messageId} attachment={{ ...a, id: a.id }} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function DownloadButton({ messageId, attachment }: { messageId: string; attachment: Attachment & { id: string } }) {
  const { config, firebase } = useClients();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      saveBlob(await fetchAttachment(window.fetch.bind(window), config.apiUrl, firebaseTokenSource(firebase.auth), messageId, attachment.id), attachment.name);
    } catch (e) {
      toast({ tone: "error", message: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={() => void download()}
      disabled={busy}
      aria-label={`Download ${attachment.name}`}
      className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs text-text hover:bg-surface-2 disabled:opacity-60"
    >
      <span aria-hidden="true">↓</span>
      {attachment.name} <span className="text-muted">· {busy ? "downloading…" : fileSize(attachment.size)}</span>
    </button>
  );
}
